import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Alert,
  Snackbar,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  LinearProgress,
  Tabs,
  Tab,
  Badge,
  Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Grid } from '../components/common/TypedGrid';
import {
  Visibility,
  Person,
  Email,
  Phone,
  Search,
  Download,
  Edit,
  Block,
  CheckCircle,
  LocationOn,
  Close,
  Assignment,
  AttachMoney,
  Insights,
} from '@mui/icons-material';
import { useAdmin } from '../contexts/AdminContext';
import { PageHeader, ResponsiveTable, SectionCard, StatCard, StatusChip } from '../components/common';
import type { ResponsiveColumn } from '../components/common';
import { exportToCSV as exportToCSVShared } from '../utils/csvExport';
import adminService, { Customer } from '../services/adminService';
import { db } from '../config/firebase';
import { collection, query, onSnapshot, orderBy, limit } from 'firebase/firestore';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`customer-tabpanel-${index}`}
    aria-labelledby={`customer-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

/** A "label above value" pair — the detail-dialog equivalent of a form field, read-only. */
const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
      {value || value === 0 ? value : <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>Not provided</Box>}
    </Typography>
  </Box>
);

const CustomerManagement: React.FC = () => {
  const { refreshDashboardStats } = useAdmin();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Issue #358: getAllCustomers() read the users collection, the tasks
  // collection and the addresses subcollection of every user — 5,001 queries
  // for 5,000 customers, on every load and again on every task write. The list
  // is now served a page at a time, per-customer totals are aggregated
  // server-side for the visible rows, and addresses load when a row is opened.
  const [cursor, setCursor] = useState<unknown | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [counts, setCounts] = useState({ all: 0, active: 0, banned: 0, deleted: 0 });

  // Export functionality
  const exportToCSV = (data: any[], filename: string) => {
    exportToCSVShared(data, filename, () =>
      setSnackbar({ open: true, message: 'No data to export', severity: 'error' })
    );
    if (data && data.length > 0) {
      setSnackbar({ open: true, message: `${filename} exported successfully!`, severity: 'success' });
    }
  };

  const handleExportCustomers = async () => {
    // The table holds one page, so the export fetches the full set itself.
    setExporting(true);
    let rows: Customer[];
    let truncated = false;
    try {
      const result = await adminService.getCustomersForExport();
      rows = result.rows;
      truncated = result.truncated;
    } catch (error) {
      console.error('Error preparing customer export:', error);
      showSnackbar('Could not prepare the export. Please try again.', 'error');
      return;
    } finally {
      setExporting(false);
    }

    if (truncated) {
      showSnackbar(
        `Export capped at ${rows.length.toLocaleString()} rows.`,
        'error'
      );
    }

    const exportData = rows.map(customer => {
      // Get phone number and ensure proper formatting for Excel (prefix with ' to prevent scientific notation)
      let phoneNumber = customer.phone || customer.phoneNumber || '';
      // Format phone number with country code if not present
      if (phoneNumber && !phoneNumber.startsWith('+')) {
        // Assume Indian number if no country code
        phoneNumber = phoneNumber.startsWith('91') ? `+${phoneNumber}` : `+91${phoneNumber}`;
      }
      // Prefix with apostrophe to ensure Excel treats it as text
      const phoneForExport = phoneNumber ? `'${phoneNumber}` : 'N/A';

      // Use customer name which now comes from profile.firstName + profile.lastName
      const customerName = customer.name || customer.displayName || 'N/A';

      // Email is not supported for customer registration, only include if exists (legacy data)
      const exportRow: Record<string, any> = {
        'Customer ID': customer.id,
        'Name': customerName,
        'Phone Number': phoneForExport,
        'Status': customer.status || 'active',
        'Total Tasks': customer.totalTasks ?? customer.totalTask ?? 0,
        'Total Spent (INR)': customer.totalSpent || 0,
        'Verified': customer.isVerified === true ? 'Yes' : 'No',
        'Joined Date': customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : 'N/A',
        'Last Active': customer.lastLoginAt ? new Date(customer.lastLoginAt).toLocaleDateString() : 'Never'
      };
      // Only include email in export if it exists
      if (customer.email) {
        exportRow['Email'] = customer.email;
      }
      return exportRow;
    });

    exportToCSV(exportData, 'customers_export');
  };

  useEffect(() => {
    reloadFirstPage();

    // Issue #358: these listeners used to watch the users and tasks
    // collections in full, so every write anywhere streamed an entire
    // collection to the browser and triggered another full reload. They only
    // need to be change signals, so each watches a single newest document.
    const watchLatest = (path: string, label: string, onChange: () => void) => {
      let first = true;
      return onSnapshot(
        query(collection(db, path), orderBy('createdAt', 'desc'), limit(1)),
        () => {
          if (first) {
            first = false; // the initial snapshot is not a change
            return;
          }
          console.log(`📊 ${label} changed — refreshing the first page`);
          onChange();
        },
        (error) => console.error(`Error listening to ${path}:`, error)
      );
    };

    const unsubscribeUsers = watchLatest('users', 'Customers', reloadFirstPage);
    const unsubscribeTasks = watchLatest('tasks', 'Tasks', reloadFirstPage);

    return () => {
      unsubscribeUsers();
      unsubscribeTasks();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, searchTerm, filterStatus, tabValue]);

  const reloadFirstPage = async () => {
    setLoading(true);
    try {
      const [page, statusCounts] = await Promise.all([
        adminService.getCustomersPage(),
        adminService.getCustomerStatusCounts(),
      ]);
      // Issue #245: newest first — now the query's ordering, not a client sort.
      setCustomers(page.rows);
      setCursor(page.cursor);
      setHasMore(page.hasMore);
      setCounts(statusCounts);
    } catch (error) {
      console.error('Error loading customers:', error);
      showSnackbar('Error loading customers', 'error');
    } finally {
      setLoading(false);
    }
  };

  /** Kept for the handlers that refresh after a mutation. */
  const loadCustomers = reloadFirstPage;

  const handleLoadMore = async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await adminService.getCustomersPage({ cursor });
      setCustomers(prev => [...prev, ...page.rows]);
      setCursor(page.cursor);
      setHasMore(page.hasMore);
    } catch (error) {
      console.error('Error loading more customers:', error);
      showSnackbar('Could not load more customers. Please try again.', 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  /**
   * Filter the rows that are loaded.
   *
   * Search cannot be pushed into the query — Firestore has no substring search.
   * The status filter cannot either, because `status` is derived from two
   * different stored fields (`accountStatus` from the mobile app, `status` on
   * older documents) with "neither set" meaning active, so no single equality
   * query reproduces it. The counts above come from the server and are exact;
   * these filters narrow what has been loaded.
   */
  const filterCustomers = () => {
    let filtered = customers;

    // Issue #245: tab status (Suspended removed, Deleted added)
    switch (tabValue) {
      case 1: // Active
        filtered = filtered.filter(customer => customer.status === 'active');
        break;
      case 2: // Banned
        filtered = filtered.filter(customer => customer.status === 'banned');
        break;
      case 3: // Deleted
        filtered = filtered.filter(customer => customer.status === 'deleted');
        break;
      default: // All
        break;
    }

    const term = searchTerm.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter(customer =>
        (customer.name || '').toLowerCase().includes(term) ||
        (customer.email || '').toLowerCase().includes(term) ||
        (customer.phone || '').includes(searchTerm.trim())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(customer => customer.status === filterStatus);
    }

    setFilteredCustomers(filtered);
  };

  /** Load a customer's saved addresses when their row is opened (issue #262). */
  const openCustomerDetails = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setDetailsOpen(true);
    if (customer.addresses && customer.addresses.length > 0) return;
    try {
      const addresses = await adminService.getCustomerAddresses(customer.id);
      setSelectedCustomer(prev => (prev && prev.id === customer.id ? { ...prev, addresses } : prev));
    } catch (error) {
      console.error('Error loading customer addresses:', error);
    }
  };

  const handleUpdateCustomerStatus = async (customerId: string, newStatus: string) => {
    try {
      await adminService.updateCustomerStatus(customerId, newStatus);
      await loadCustomers();
      await refreshDashboardStats();
      showSnackbar(`Customer ${newStatus} successfully`, 'success');
      setDetailsOpen(false);
    } catch (error) {
      console.error('Error updating customer status:', error);
      showSnackbar('Error updating customer status', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // Issue #245: Updated tab counts to replace Suspended with Deleted
  // Issue #358: exact counts from server-side count queries, so the badges do
  // not silently mean "of the rows loaded so far".
  const tabCounts = counts;

  return (
    <Box>
      <PageHeader title="Customers" subtitle="Everyone who has booked through the app" />

      <Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard label="Total Customers" value={tabCounts.all} icon={<Person />} tone="primary" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Active" value={tabCounts.active} icon={<CheckCircle />} tone="success" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Banned" value={tabCounts.banned} icon={<Block />} tone="error" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Deleted" value={tabCounts.deleted} icon={<Close />} tone="warning" />
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search loaded customers..."
              helperText="Searches the rows loaded below."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="banned">Banned</MenuItem>
                <MenuItem value="deleted">Deleted</MenuItem>
              </Select>
            </FormControl>
            <Button
              startIcon={<Download />}
              variant="outlined"
              onClick={handleExportCustomers}
              disabled={exporting}
            >
              {exporting ? 'Preparing export…' : 'Export'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Issue #245: Tabs - Removed Suspended, Added Deleted */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab
              label={
                <Badge badgeContent={tabCounts.all} color="primary" showZero>
                  All Customers
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={tabCounts.active} color="success" showZero>
                  Active
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={tabCounts.banned} color="error" showZero>
                  Banned
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={tabCounts.deleted} color="warning" showZero>
                  Deleted
                </Badge>
              }
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <CustomerTable 
            customers={filteredCustomers} 
            onViewDetails={openCustomerDetails}
            onUpdateStatus={handleUpdateCustomerStatus}
            loading={loading}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <CustomerTable 
            customers={filteredCustomers} 
            onViewDetails={openCustomerDetails}
            onUpdateStatus={handleUpdateCustomerStatus}
            loading={loading}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <CustomerTable 
            customers={filteredCustomers} 
            onViewDetails={openCustomerDetails}
            onUpdateStatus={handleUpdateCustomerStatus}
            loading={loading}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <CustomerTable 
            customers={filteredCustomers} 
            onViewDetails={openCustomerDetails}
            onUpdateStatus={handleUpdateCustomerStatus}
            loading={loading}
          />
        </TabPanel>

        {/* Issue #358: the table holds loaded pages, not the whole collection. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 3,
            py: 2,
            borderTop: 1,
            borderColor: 'divider',
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {`Showing ${filteredCustomers.length.toLocaleString()} of ${tabCounts[
              tabValue === 1 ? 'active' : tabValue === 2 ? 'banned' : tabValue === 3 ? 'deleted' : 'all'
            ].toLocaleString()}`}
          </Typography>
          {hasMore && (
            <Button onClick={handleLoadMore} disabled={loadingMore} variant="outlined" size="small">
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          )}
        </Box>
      </Card>

      {/* Customer Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            py: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}>
              <Person fontSize="small" />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 1, lineHeight: 1 }} display="block">
                Customer Details
              </Typography>
              <Typography variant="h6" fontWeight={700} noWrap>
                {selectedCustomer?.name || 'Unknown Customer'}
              </Typography>
              {selectedCustomer?.email && (
                <Typography variant="caption" sx={{ opacity: 0.75 }} noWrap display="block">
                  {selectedCustomer.email}
                </Typography>
              )}
            </Box>
          </Stack>
          <IconButton
            onClick={() => setDetailsOpen(false)}
            sx={{ color: 'white', flexShrink: 0 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedCustomer  && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Personal Information */}
              <SectionCard title="Personal Information" icon={<Person fontSize="small" />} tone="primary">
                <Grid container spacing={2.5}>
                  <Grid item xs={6} sm={4}>
                    <Field label="Full Name" value={selectedCustomer.name || 'Unknown Customer'} />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Field label="Phone" value={selectedCustomer.phone} />
                  </Grid>
                  {/* Only show email if it exists - email is not supported for customer registration */}
                  {selectedCustomer.email && (
                    <Grid item xs={6} sm={4}>
                      <Field label="Email" value={selectedCustomer.email} />
                    </Grid>
                  )}
                  {/* Issue #215, #256: Display customer address from addresses with alias priority */}
                  <Grid item xs={12}>
                    <Field
                      label="Address"
                      value={
                        // Issue #256: Priority-based address resolution from fullAddress field
                        (() => {
                          const addresses = (selectedCustomer as any).addresses || [];

                          // Priority 1: alias = "Home" (exact match, capital H)
                          const homeCapital = addresses.find((addr: any) => addr.alias === 'Home');
                          if (homeCapital?.fullAddress) return homeCapital.fullAddress;

                          // Priority 2: alias = "home" (exact match, lowercase h)
                          const homeLower = addresses.find((addr: any) => addr.alias === 'home');
                          if (homeLower?.fullAddress) return homeLower.fullAddress;

                          // Priority 3: Any home-type variant (home1, Home1, etc.)
                          const homeVariant = addresses.find((addr: any) =>
                            addr.alias && addr.alias.toLowerCase().startsWith('home')
                          );
                          if (homeVariant?.fullAddress) return homeVariant.fullAddress;

                          // Fallback: currentLocation.address or any address
                          return (selectedCustomer as any).currentLocation?.address ||
                                 addresses.find((addr: any) => addr.isDefault)?.fullAddress ||
                                 addresses[0]?.fullAddress ||
                                 undefined;
                        })()
                      }
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Field label="Member Since" value={new Date(selectedCustomer.createdAt).toLocaleDateString()} />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Field label="Last Active" value={selectedCustomer.lastLoginAt ? new Date(selectedCustomer.lastLoginAt).toLocaleDateString() : 'Never'} />
                  </Grid>
                </Grid>
              </SectionCard>

              {/* Account Status & Activity */}
              <SectionCard title="Account Status & Activity" icon={<Insights fontSize="small" />} tone="secondary">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Status:</Typography>
                    <StatusChip status={selectedCustomer.status} size="medium" />
                  </Box>

                  {/* Issue #239: Show deletion information for deleted accounts */}
                  {selectedCustomer.status === 'deleted' && (
                    <Box sx={{
                      p: 2,
                      backgroundColor: (t) => alpha(t.palette.error.main, 0.1),
                      borderRadius: 1,
                      borderLeft: '4px solid',
                      borderColor: 'error.main'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main', mb: 1 }}>
                        Account Deleted
                      </Typography>
                      {selectedCustomer.deletedAt && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Deleted On:</strong> {new Date(selectedCustomer.deletedAt).toLocaleString()}
                        </Typography>
                      )}
                      {selectedCustomer.deletionReason && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Reason:</strong> {selectedCustomer.deletionReason}
                        </Typography>
                      )}
                      {selectedCustomer.deletionDetails && (
                        <Typography variant="body2">
                          <strong>Details:</strong> {selectedCustomer.deletionDetails}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Grid container spacing={2.5}>
                    <Grid item xs={6}>
                      <Box sx={{ 
                        p: 2.5, 
                        border: '1px solid', 
                        borderColor: 'divider', 
                        borderRadius: 2, 
                        textAlign: 'center',
                        backgroundColor: (t) => alpha(t.palette.primary.main, 0.1),
                      }}>
                        <Assignment color="primary" sx={{ fontSize: 28, mb: 1 }} />
                        <Typography variant="h5" fontWeight={700}>
                          {selectedCustomer.totalTasks}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Total Tasks
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ 
                        p: 2.5, 
                        border: '1px solid', 
                        borderColor: 'divider', 
                        borderRadius: 2, 
                        textAlign: 'center',
                        backgroundColor: (t) => alpha(t.palette.success.main, 0.1),
                      }}>
                        <AttachMoney color="success" sx={{ fontSize: 28, mb: 1 }} />
                        <Typography variant="h5" fontWeight={700}>
                          ₹{selectedCustomer.totalSpent.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Total Spent
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </SectionCard>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          px: 3, 
          py: 2, 
          backgroundColor: 'action.hover',
          justifyContent: 'flex-end',
          gap: 1
        }}>
          {selectedCustomer && (
            <>
              {selectedCustomer.status === 'active' && (
                <Button 
                  onClick={() => selectedCustomer && handleUpdateCustomerStatus(selectedCustomer.id, 'banned')}
                  color="error"
                  variant="outlined"
                  startIcon={<Block />}
                  size="small"
                >
                  Block Customer
                </Button>
              )}
              {selectedCustomer.status === 'banned' && (
                <Button 
                  onClick={() => selectedCustomer && handleUpdateCustomerStatus(selectedCustomer.id, 'active')}
                  color="success"
                  variant="contained"
                  startIcon={<CheckCircle />}
                  size="small"
                >
                  Unblock Customer
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

interface CustomerTableProps {
  customers: Customer[];
  onViewDetails: (customer: Customer) => void;
  onUpdateStatus: (customerId: string, status: string) => void;
  loading: boolean;
}

const CustomerTable: React.FC<CustomerTableProps> = ({ 
  customers, 
  onViewDetails, 
  onUpdateStatus, 
  loading
}) => {
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading customers...</Typography>
      </Box>
    );
  }

  const columns: ResponsiveColumn<any>[] = [
    {
      key: 'customer',
      header: 'Customer',
      mobile: 'title',
      render: (customer) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ mr: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            {(customer.name || 'U').charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {customer?.name || 'Unknown Customer'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {customer.id.substring(0, 8)}…
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (customer) => (
        <>
          {/* Phone is the primary contact; email is rare/legacy for customers. */}
          <Typography variant="body2">{customer.phone || 'No phone'}</Typography>
          {customer.email && (
            <Typography variant="caption" color="text.secondary">
              {customer.email}
            </Typography>
          )}
        </>
      ),
    },
    {
      key: 'tasks',
      header: 'Tasks',
      render: (customer) => (
        <Typography variant="body2" fontWeight={600}>
          {customer.totalTasks}
        </Typography>
      ),
    },
    {
      key: 'spent',
      header: 'Total Spent',
      render: (customer) => (
        <Typography variant="body2" fontWeight={600}>
          ₹{customer.totalSpent.toLocaleString()}
        </Typography>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (customer) => <StatusChip status={customer.status} />,
    },
    {
      key: 'joined',
      header: 'Joined',
      render: (customer) => (
        <Typography variant="body2">
          {new Date(customer.createdAt).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      mobile: 'actions',
      render: (customer) => (
        <IconButton
          onClick={() => onViewDetails(customer)}
          size="small"
          aria-label="View customer details"
        >
          <Visibility />
        </IconButton>
      ),
    },
  ];

  return (
    <ResponsiveTable
      columns={columns}
      rows={customers}
      rowKey={(customer) => customer.id}
      loading={loading}
      onRowClick={onViewDetails}
      aria-label="Customers"
      emptyTitle="No customers found"
      emptyDescription="Customers appear here once people sign up and book their first job."
    />
  );
};

export default CustomerManagement;